import { useTranslation } from 'react-i18next';
import useCartValidation from 'checkout/useCartValidation';
import { useCart } from 'context/CartContext';
import formatIDR from 'utils/formatIDR';
import Button from './Button';
import CartLine from './CartLine';
import CartNotices from './CartNotices';
import Drawer from './Drawer';
import { BagIcon } from './icons';

export default function CartDrawer() {
  const { t } = useTranslation();
  const { lines, count, subtotal, drawerOpen, closeDrawer } = useCart();
  const empty = lines.length === 0;
  // Only while the drawer is open: validating costs a request.
  const validation = useCartValidation({ enabled: drawerOpen });
  const data = validation.data;
  const shownSubtotal = data && data.pricing ? data.pricing.subtotalAmount : subtotal;
  const blocked = data ? data.hasBlocking : false;

  return (
    <Drawer
      open={drawerOpen}
      onClose={closeDrawer}
      title={count ? t('cart.drawerTitleCount', { count }) : t('cart.drawerTitle')}
      footer={
        !empty && (
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-[15px]">{t('cart.subtotal')}</span>
              <span className="text-lg font-semibold tabular-nums" aria-busy={!validation.current || undefined}>
                {formatIDR(shownSubtotal)}
              </span>
            </div>
            <p className="mt-1 text-[13px] text-muted">{t('cart.shippingAtCheckout')}</p>
            {blocked && (
              <p id="drawer-blocked" className="mt-2 text-[13px] text-sale">
                {t('cart.checkoutBlocked')}
              </p>
            )}
            <div className="mt-4 grid gap-2">
              {blocked ? (
                <Button block disabled aria-describedby="drawer-blocked">
                  {t('cart.checkout')}
                </Button>
              ) : (
                <Button to="/checkout" block onClick={closeDrawer}>
                  {t('cart.checkout')}
                </Button>
              )}
              <Button to="/keranjang" variant="secondary" block onClick={closeDrawer}>
                {t('cart.viewCart')}
              </Button>
            </div>
          </div>
        )
      }
    >
      {empty ? (
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-surface-alt">
            <BagIcon className="h-6 w-6" />
          </span>
          <p className="font-display text-2xl">{t('cart.emptyTitle')}</p>
          <p className="mt-2 max-w-xs text-muted">{t('cart.emptyBody')}</p>
          <Button to="/kategori/semua" className="mt-6" onClick={closeDrawer}>
            {t('cart.startShopping')}
          </Button>
        </div>
      ) : (
        <>
          <div className="px-5 pt-4 empty:hidden">
            <CartNotices validation={validation} />
          </div>
          <ul className="divide-y divide-border px-5" aria-label={t('cart.itemsLabel')}>
            {lines.map((line) => (
              <CartLine
                key={line.variantId}
                line={line}
                check={data ? data.lines[line.variantId] : null}
                onNavigate={closeDrawer}
                compact
              />
            ))}
          </ul>
        </>
      )}
    </Drawer>
  );
}
